import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, PencilLine } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

// Request a change to a qualification answer (does not edit it silently).
// Logs a timeline event and notifies managers for review.
export default function QualEditRequestModal({ open, onOpenChange, leadId, items = [], onSent }) {
  const [questionLabel, setQuestionLabel] = useState("");
  const [proposed, setProposed] = useState("");
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) { setQuestionLabel(items[0]?.question_label || ""); setProposed(""); setReason(""); }
  }, [open, items]);

  const current = items.find((i) => i.question_label === questionLabel);

  const submit = async () => {
    if (!questionLabel) return toast.error("Select a question");
    if (!proposed.trim()) return toast.error("Enter the proposed answer");
    if (!reason.trim()) return toast.error("A reason is required");
    setSending(true);
    try {
      await api.post(`/leads/${leadId}/qualification-edit-request`, {
        question_label: questionLabel,
        current_answer: current?.answer || null,
        proposed_answer: proposed.trim(),
        reason: reason.trim(),
      });
      toast.success("Edit request sent for review");
      onOpenChange(false);
      onSent?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-sky bg-white sm:max-w-md" data-testid="qual-edit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-sora text-navy"><PencilLine className="h-5 w-5 text-loop" />Request Qualification Edit</DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-xs text-ink/60">Qualification answers can't be edited directly. Your request is logged on the lead timeline and sent to a manager for review.</DialogDescription>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink/60">Question</label>
            <select data-testid="qual-edit-question" value={questionLabel} onChange={(e) => setQuestionLabel(e.target.value)} className="h-10 w-full rounded-lg border border-sky px-2 text-sm outline-none focus:border-loop">
              {items.map((i) => <option key={i.question_label} value={i.question_label}>{i.question_label}</option>)}
            </select>
          </div>
          <div className="rounded-lg bg-mist px-3 py-2 text-sm">
            <span className="text-xs text-ink/50">Current answer:</span> <span className="font-medium text-navy">{current?.answer || "—"}</span>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink/60">Proposed answer</label>
            <input data-testid="qual-edit-proposed" value={proposed} onChange={(e) => setProposed(e.target.value)} placeholder="What should it be?" className="h-10 w-full rounded-lg border border-sky px-3 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink/60">Reason</label>
            <textarea data-testid="qual-edit-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Why does this need to change?" className="w-full rounded-lg border border-sky p-3 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" />
          </div>
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <button onClick={() => onOpenChange(false)} className="rounded-lg border border-sky px-4 py-2 text-sm font-medium text-ink/70 hover:bg-mist">Cancel</button>
          <button onClick={submit} disabled={sending} data-testid="qual-edit-submit" className="flex items-center gap-1.5 rounded-lg bg-loop px-4 py-2 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">
            {sending && <Loader2 className="h-4 w-4 animate-spin" />}Send request
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
